import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import {
  buildPageLeavePayload,
  buildPageViewPayload,
  newClientEventId,
  shouldTrackPathname,
  sendPageLeave,
  sendPageView,
} from "../utils/pageView";

/**
 * Records route changes as page views and page_leave with dwell time.
 *
 * - Set `REACT_APP_PAGE_VIEW_API_URL` in `.env.local` (POST JSON).
 * - To track only Activities first: `REACT_APP_PAGE_VIEW_TRACK_PREFIXES=/Activities`
 *   (comma-separated for multiple prefixes). If unset, all paths are tracked.
 *
 * Payload shapes: `PageViewPayload` / `PageLeavePayload` in `src/utils/pageView.ts`.
 */
export function PageViewTracker() {
  const location = useLocation();
  const activeRef = useRef<{
    clientEventId: string;
    pathname: string;
    fullPath: string;
    enteredAtMs: number;
    viewSequence: number;
  } | null>(null);

  useEffect(() => {
    const onPageHide = () => {
      const cur = activeRef.current;
      if (!cur || !shouldTrackPathname(cur.pathname)) {
        return;
      }
      sendPageLeave(
        buildPageLeavePayload(
          cur.pathname,
          cur.fullPath,
          cur.enteredAtMs,
          cur.clientEventId,
          cur.viewSequence
        )
      );
      activeRef.current = null;
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  useEffect(() => {
    const { pathname, search, hash } = location;
    const fullPath = `${pathname}${search}${hash}`;
    const prev = activeRef.current;

    if (prev) {
      const sameView = prev.fullPath === fullPath;
      if (sameView) {
        return;
      }
      if (shouldTrackPathname(prev.pathname)) {
        sendPageLeave(
          buildPageLeavePayload(
            prev.pathname,
            prev.fullPath,
            prev.enteredAtMs,
            prev.clientEventId,
            prev.viewSequence
          )
        );
      }
      activeRef.current = null;
    }

    if (!shouldTrackPathname(pathname)) {
      return;
    }

    const clientEventId = newClientEventId();
    const payload = buildPageViewPayload(pathname, search, hash, clientEventId);
    if (!sendPageView(payload)) {
      return;
    }

    ReactGA.send({ hitType: "pageview", page: fullPath, title: document.title });

    activeRef.current = {
      clientEventId,
      pathname,
      fullPath,
      enteredAtMs: Date.now(),
      viewSequence: payload.viewSequence,
    };
  }, [location]);

  return null;
}
